import { Injectable, ElementRef, OnDestroy } from '@angular/core';
import * as THREE from 'three';
import OrbitControls from 'three-orbitcontrols';

var STLLoader = require('three-stl-loader')(THREE);

@Injectable()
export class ThreeService implements OnDestroy {

    private canvas: HTMLCanvasElement;
    private renderer: THREE.WebGLRenderer;
    private camera: THREE.PerspectiveCamera;
    private scene: THREE.Scene;
    private controls: OrbitControls;

    private frameId: number = null;

    public constructor() { }

    public ngOnDestroy() {

        if (this.frameId != null) {
            cancelAnimationFrame(this.frameId);
        }

    }

    createSTLScene(element: ElementRef, file: any): void {

        this.canvas = <HTMLCanvasElement>element.nativeElement;

        this.scene = new THREE.Scene();
        this.scene.add(new THREE.AmbientLight(0x999999));

        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 1, 500);

        // Z is up for objects intended to be 3D printed.

        this.camera.up.set(0, 0, 1);
        this.camera.position.set(0, -9, 6);

        this.camera.add(new THREE.PointLight(0xffffff, 0.8));

        this.scene.add(this.camera);

        var grid = new THREE.GridHelper(25, 50, 0xffffff, 0x555555);
        grid.rotateOnAxis(new THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180));
        this.scene.add(grid);

        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true
        });

        this.renderer.setClearColor(0x999999);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(window.innerWidth, window.innerHeight);

        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true; // an animation loop is required when either damping or auto-rotation are enabled
        this.controls.enableZoom = true;
        this.controls.isPerspectiveCamera = true;

        var material = new THREE.MeshPhongMaterial({ color: 0xe5acb6, specular: 0x111111, shininess: 200 });

        var t = this;
        var loader= new STLLoader();
        loader.load(file, function (geometry) {

            var mesh = new THREE.Mesh(geometry, material);
            mesh.position.set(0, 0, 0);
            mesh.rotation.set(0, 0, 0);
            mesh.scale.set(.1, .1, .1);

            mesh.castShadow = true;
            mesh.receiveShadow = true;

            t.scene.add(mesh);

            t.animate();

        });

    }
    
    getSTLDetails(file: any): Promise<any> {

        var t = this;

        return new Promise<any>((resolve, reject) => {

            var loader = new STLLoader();
            loader.load(file, function (geometry) {

                var d = t.getDetails(geometry);
                var size = t.getSize(geometry);

                resolve({
                    x: (size.x / 10).toFixed(2),
                    y: (size.y / 10).toFixed(2),
                    z: (size.z / 10).toFixed(2),
                    v: (d.v / 1000).toFixed(2),
                    a: (d.a / 100).toFixed(2)
                });

            });

        });

    }

    getResizedSTLDetails(file: any, nx: number, ny: number, nz: number, filename: string): Promise<any> {

        var t = this;

        var scene = new THREE.Scene();

        return new Promise<any>((resolve, reject) => {

            var loader = new STLLoader();
            loader.load(file, function (geometry) {

                var original_size = t.getSize(geometry);

                var rx = nx / (original_size.x / 10);
                var ry = ny / (original_size.y / 10);
                var rz = nz / (original_size.z / 10);

                geometry.scale(rx, ry, rz);

                var d = t.getDetails(geometry);
                var size = t.getSize(geometry);

                var m = new THREE.Mesh(new THREE.Geometry().fromBufferGeometry(geometry));
                m.name = "resized_" + filename;
                m.position.set(0, 0, 0);
                scene.add(m);

                var blob = t.getResizedFile(scene);

                resolve({
                    x: (size.x / 10).toFixed(2),
                    y: (size.y / 10).toFixed(2),
                    z: (size.z / 10).toFixed(2),
                    v: (d.v / 1000).toFixed(2),
                    a: (d.a / 100).toFixed(2),
                    f: blob
                });

            });

        });

    }

    getDetails(geometry) {

        let position = geometry.attributes.position;
        let faces = position.count / 3;

        let area = 0;
        let volume = 0;

        let p1 = new THREE.Vector3(),
            p2 = new THREE.Vector3(),
            p3 = new THREE.Vector3(),
            cross = new THREE.Vector3();

        for (let i = 0; i < faces; i++) {

            p1.fromBufferAttribute(position, i * 3 + 0);
            p2.fromBufferAttribute(position, i * 3 + 1);
            p3.fromBufferAttribute(position, i * 3 + 2);

            var ab = { x: p2.x - p1.x, y: p2.y - p1.y, z: p2.z - p1.z };
            var ac = { x: p3.x - p1.x, y: p3.y - p1.y, z: p1.z - p3.z };

            volume += this.signedVolumeOfTriangle(p1, p2, p3);

            cross = this.crossVectors(ab, ac);
            area += Math.sqrt(Math.pow(cross.x, 2) + Math.pow(cross.y, 2) + Math.pow(cross.z, 2)) / 2;
        }

        return { v: volume, a: area };

    }

    getVolume(geometry) {

        let position = geometry.attributes.position;
        let faces = position.count / 3;
        let sum = 0;

        let p1 = new THREE.Vector3(),
            p2 = new THREE.Vector3(),
            p3 = new THREE.Vector3();

        for (let i = 0; i < faces; i++) {
            p1.fromBufferAttribute(position, i * 3 + 0);
            p2.fromBufferAttribute(position, i * 3 + 1);
            p3.fromBufferAttribute(position, i * 3 + 2);
            sum += this.signedVolumeOfTriangle(p1, p2, p3);
        }

        return sum;

    }

    getSurface(geometry) {

        let position = geometry.attributes.position;
        let faces = position.count / 3;
        let sum = 0;

        let p1 = new THREE.Vector3(),
            p2 = new THREE.Vector3(),
            p3 = new THREE.Vector3();

        for (let i = 0; i < faces; i++) {

            p1.fromBufferAttribute(position, i * 3 + 0);
            p2.fromBufferAttribute(position, i * 3 + 1);
            p3.fromBufferAttribute(position, i * 3 + 2);

            var ab = { x: p2.x - p1.x, y: p2.y - p1.y, z: p2.z - p1.z };
            var ac = { x: p3.x - p1.x, y: p3.y - p1.y, z: p1.z - p3.z };
            var cross = new THREE.Vector3();

            cross = this.crossVectors(ab, ac);
            sum += Math.sqrt(Math.pow(cross.x, 2) + Math.pow(cross.y, 2) + Math.pow(cross.z, 2)) / 2;
        }

        return sum;

    }

    getSize(geometry) {

        let size = new THREE.Vector3();
        geometry.computeBoundingBox();
        geometry.boundingBox.getSize(size);
        return size;

    }

    crossVectors(a, b) {

        var ax = a.x, ay = a.y, az = a.z;
        var bx = b.x, by = b.y, bz = b.z;

        var P = {
            x: ay * bz - az * by,
            y: az * bx - ax * bz,
            z: ax * by - ay * bx
        }

        return P;
    }

    signedVolumeOfTriangle(p1, p2, p3) {
        return p1.dot(p2.cross(p3)) / 6.0;
    }

    getResizedFile(scene) {

        var vector = new THREE.Vector3();
        var normalMatrixWorld = new THREE.Matrix3();

        var output = "solid exported\n";

        scene.traverse(function (mesh) {

            if (mesh instanceof THREE.Mesh) {

                var geometry = mesh.geometry;
                var matrixWorld = mesh.matrixWorld;

                if (geometry instanceof THREE.Geometry) {

                    var vertices = geometry.vertices;
                    var faces = geometry.faces;

                    normalMatrixWorld.getNormalMatrix(matrixWorld);

                    for (var i = 0, l = faces.length; i < l; i++) {
                        var face = faces[i];

                        vector.copy(face.normal).applyMatrix3(normalMatrixWorld).normalize();

                        output += '\tfacet normal ' + vector.x + ' ' + vector.y + ' ' + vector.z + '\n';
                        output += '\t\touter loop\n';

                        var indices = [face.a, face.b, face.c];

                        for (var j = 0; j < 3; j++) {

                            var vertexIndex = indices[j];

                            if (mesh.geometry.skinIndices.length == 0) {

                                vector.copy(vertices[vertexIndex]).applyMatrix4(matrixWorld);
                                output += '\t\t\tvertex ' + vector.x + ' ' + vector.y + ' ' + vector.z + '\n';

                            } else {

                                vector.copy(vertices[vertexIndex]); //.applyMatrix4( matrixWorld );

                                var boneIndices = [];
                                boneIndices[0] = mesh.geometry.skinIndices[vertexIndex].x;
                                boneIndices[1] = mesh.geometry.skinIndices[vertexIndex].y;
                                boneIndices[2] = mesh.geometry.skinIndices[vertexIndex].z;
                                boneIndices[3] = mesh.geometry.skinIndices[vertexIndex].w;

                                var weights = [];
                                weights[0] = mesh.geometry.skinWeights[vertexIndex].x;
                                weights[1] = mesh.geometry.skinWeights[vertexIndex].y;
                                weights[2] = mesh.geometry.skinWeights[vertexIndex].z;
                                weights[3] = mesh.geometry.skinWeights[vertexIndex].w;

                                var inverses = [];
                                inverses[0] = mesh.skeleton.boneInverses[boneIndices[0]];
                                inverses[1] = mesh.skeleton.boneInverses[boneIndices[1]];
                                inverses[2] = mesh.skeleton.boneInverses[boneIndices[2]];
                                inverses[3] = mesh.skeleton.boneInverses[boneIndices[3]];

                                var skinMatrices = [];
                                skinMatrices[0] = mesh.skeleton.bones[boneIndices[0]].matrixWorld;
                                skinMatrices[1] = mesh.skeleton.bones[boneIndices[1]].matrixWorld;
                                skinMatrices[2] = mesh.skeleton.bones[boneIndices[2]].matrixWorld;
                                skinMatrices[3] = mesh.skeleton.bones[boneIndices[3]].matrixWorld;

                                var finalVector = new THREE.Vector4();

                                for (var k = 0; k < 4; k++) {

                                    var tempVector = new THREE.Vector4(vector.x, vector.y, vector.z);
                                    tempVector.multiplyScalar(weights[k]);
                                    //the inverse takes the vector into local bone space
                                    tempVector.applyMatrix4(inverses[k])
                                        //which is then transformed to the appropriate world space
                                        .applyMatrix4(skinMatrices[k]);
                                    finalVector.add(tempVector);

                                }
                                output += '\t\t\tvertex ' + finalVector.x + ' ' + finalVector.y + ' ' + finalVector.z + '\n';
                            }
                        }
                        output += '\t\tendloop\n';
                        output += '\tendfacet\n';
                    }
                }
            }

        });

        output += 'endsolid exported\n';

        return new Blob([output], { type: 'text/plain' });

    }

    animate(): void {
        requestAnimationFrame(this.animate.bind(this));
        this.controls.update(); // only required if controls.enableDamping = true, or if controls.autoRotate = true
        this.render();
    }

    render() {
        this.renderer.render(this.scene, this.camera);
    }

}


// WEBPACK FOOTER //
// ./ClientApp/app/_services/three.service.ts